import { combineSlices, configureStore, type Reducer } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query/react'

import { userStorage } from '~/shared/storage'
import { baseApi } from '~/shell/api/rtk/baseApi'

import { appScopeSlice, appSessionChanged } from './appScope'
import { forbiddenMiddleware } from './forbiddenMiddleware'
import { forbiddenSlice } from './forbiddenSlice'
import { unauthorizedMiddleware } from './unauthorizedMiddleware'

/** Augmented by apps using type-only imports, without eagerly loading their code. */
export interface LazyAppState {}

/**
 * Creates the suite's shared Redux store and RTK Query cache.
 *
 * Apps register reducers lazily. Their state survives app navigation within
 * the same customer and session, but resets when either changes.
 * App state stays at its initial value while the scope is missing or invalid.
 *
 * See Atlas ADR-0030 for app state ownership.
 */
export const createStore = () => {
  const combined = combineSlices(
    baseApi,
    forbiddenSlice,
    appScopeSlice,
  ).withLazyLoadedSlices<LazyAppState>()

  const appReducers = new Map<string, Reducer>()
  const reducer: Reducer<ReturnType<typeof combined>> = (state, action) => {
    // Reduce first so scope changes are visible. Reset afterwards so an action
    // arriving while the scope is invalid cannot repopulate app state.
    const nextState = combined(state, action)
    const scope = nextState.appScope

    if (
      // App state cannot be retained until both customer and session are established.
      scope.customer === null ||
      scope.session === null ||
      // Discard state belonging to the previous customer or session.
      scope.customer !== state?.appScope.customer ||
      scope.session !== state?.appScope.session ||
      // The key identifies the user and storage generation. A mismatch means
      // storage ownership was invalidated, potentially by another tab.
      scope.session !== userStorage.sessionKey()
    ) {
      const resetState = { ...nextState }
      // Passing undefined recreates each app's initial state. Shell slices remain
      // intact here; CustomerLayout separately handles API-cache and 403 cleanup.
      for (const [id, appReducer] of appReducers) {
        ;(resetState as Record<string, unknown>)[id] = appReducer(undefined, {
          type: '@@atlas/appReset',
        })
      }
      return resetState
    }

    return nextState
  }

  const suiteStore = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        baseApi.middleware,
        forbiddenMiddleware,
        unauthorizedMiddleware,
      ),
  })

  const stopApiListeners = setupListeners(suiteStore.dispatch)
  // Logout can happen outside the customer route tree. Store lifetime, rather than
  // a mounted page, therefore owns invalidation of dormant app state.
  const syncSession = () => {
    const session = userStorage.sessionKey()
    if (suiteStore.getState().appScope.session !== session) {
      suiteStore.dispatch(appSessionChanged(session))
    }
  }
  const stopOwnershipListener = userStorage.subscribe(syncSession)
  syncSession()

  return Object.assign(suiteStore, {
    dispose: () => {
      stopOwnershipListener()
      stopApiListeners()
    },
    registerAppReducer: (id: string, appReducer: Reducer) => {
      if (appReducers.has(id)) return
      if (id in suiteStore.getState()) {
        throw new Error(`App reducer "${id}" conflicts with shell state`)
      }
      appReducers.set(id, appReducer)
      combined.inject({ reducerPath: id, reducer: appReducer })
      // Injection takes effect on the next action; dispatch now to initialize
      // the app's state before its components render.
      suiteStore.dispatch({ type: '@@atlas/appRegistered' })
    },
  })
}

export const store = createStore()

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type SuiteStore = ReturnType<typeof createStore>
